/**
 * "AI test call" as a button: on the company card and on the brain card.
 *
 * It opens the dialog (`AiTestCallHost`), which shows what the AI would say and
 * know before anything rings. It only ever leads to a test number, also with
 * test mode off; the server holds that line, not this button.
 *
 * Drawn only for `manage:crm.calls`, so a viewer without that right never
 * triggers a refused request on every company they open. A disabled button says
 * why in words beside it rather than in a tooltip: a disabled button fires no
 * pointer events, so its `title` would never show. The verdict comes from a
 * status cached for a minute, because every company page would otherwise ask.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneOutgoing } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getAiOutboundStatusCached, testCallGate, type AiOutboundStatus } from "../../../lib/aiOutbound";
import type { AiTestCallTarget } from "../../../lib/aiTestCallPreview";
import { useAiTestCall } from "./AiTestCallHost";

export function AiTestCallButton({ target, className }: { target: AiTestCallTarget; className?: string }) {
  const t = useT();
  const { canCall, open } = useAiTestCall();
  const [status, setStatus] = useState<AiOutboundStatus | null | "loading">("loading");
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (canCall) {
      getAiOutboundStatusCached()
        .then((next) => {
          if (alive.current) setStatus(next);
        })
        .catch(() => {
          if (alive.current) setStatus(null);
        });
    }
    return () => {
      alive.current = false;
    };
  }, [canCall]);

  if (!canCall) return null;
  const gate = status === "loading" ? null : testCallGate(status);

  return (
    <div className={className ?? "flex min-w-0 flex-wrap items-center gap-2"}>
      <Button variant="outline" size="sm" onClick={() => open(target)} disabled={!gate?.ok}>
        <PhoneOutgoing className="size-3.5" />
        {t("aiOutbound.testCall.button")}
      </Button>
      {gate && !gate.ok ? (
        <span className="text-xs text-muted-foreground">
          {t("aiOutbound.testCall.notReady", {
            reason: t(`aiOutbound.testCall.reason.${gate.reason}`, { code: gate.code }),
          })}{" "}
          <Link to="/settings/phone/ai-outbound" className="text-primary hover:underline">
            {t("aiOutbound.testCall.settingsLink")}
          </Link>
        </span>
      ) : null}
    </div>
  );
}
